import { Link, NavLink } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Search, Menu, X, Bookmark, LogOut } from "lucide-react";
import { useUser } from "../lib/userAuth";

export default function Navbar({ transparentOnHero = false, withSearch = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, signIn, signOut } = useUser();
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 32);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const overlay = transparentOnHero && !scrolled;
  const textColor = overlay ? "text-white" : "text-stone-900";
  const navClass = overlay ? "nav-transparent" : "nav-solid";

  return (
    <header data-testid="navbar" className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${navClass}`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center gap-6">
        <Link to="/" data-testid="nav-logo" className={`flex items-center gap-2 font-extrabold tracking-tight text-xl ${textColor}`}>
          <LogoMark color={overlay ? "white" : "#0E1411"} />
          <span>WaterfallFinder</span>
        </Link>

        {withSearch && !overlay && (
          <Link to="/waterfalls" className="hidden md:flex search-pill flex-1 max-w-2xl" data-testid="nav-search-pill">
            <Search className="w-4 h-4 text-stone-500 mr-2" />
            <span className="text-stone-500 text-sm">Search</span>
          </Link>
        )}

        <nav className="hidden md:flex items-center gap-7 ml-auto">
          <NavItem to="/waterfalls" label="Explore" overlay={overlay} />
          <NavItem to="/best-waterfalls" label="Best of" overlay={overlay} />
          <NavItem to="/safety" label="Safety" overlay={overlay} />
          {user && (
            <NavLink to="/saved" data-testid="nav-saved" className={`text-sm font-semibold ${textColor} hover:opacity-80 flex items-center gap-1`}>
              <Bookmark className="w-4 h-4" /> Saved
            </NavLink>
          )}
          {user ? (
            <div className="relative" ref={menuRef}>
              <button onClick={() => setMenuOpen(v => !v)} data-testid="nav-user-menu" className="flex items-center gap-2">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-stone-700 font-semibold text-sm">
                    {(user.name || user.email || "?")[0]?.toUpperCase()}
                  </div>
                )}
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-stone-200 p-2 text-sm">
                  <div className="px-3 py-2 border-b border-stone-100">
                    <p className="font-semibold text-stone-900 truncate">{user.name}</p>
                    <p className="text-xs text-stone-500 truncate">{user.email}</p>
                  </div>
                  <Link to="/saved" onClick={() => setMenuOpen(false)} className="block px-3 py-2 hover:bg-stone-50 rounded-md text-stone-900" data-testid="menu-saved">Saved waterfalls</Link>
                  <button onClick={() => { setMenuOpen(false); signOut(); }} data-testid="menu-signout" className="w-full text-left px-3 py-2 hover:bg-stone-50 rounded-md text-stone-900 flex items-center gap-2">
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => signIn(null, "Sign in to save your favourite waterfalls, track ones you've visited, leave reviews and ask questions.")} data-testid="nav-signin-google" className="btn-at btn-at-green">
              Sign in
            </button>
          )}
        </nav>

        <button className={`md:hidden ml-auto ${textColor}`} onClick={() => setOpen(v => !v)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-stone-200 px-4 py-4 flex flex-col gap-3">
          <Link to="/waterfalls" onClick={() => setOpen(false)} className="font-semibold text-stone-900">Explore</Link>
          <Link to="/best-waterfalls" onClick={() => setOpen(false)} className="font-semibold text-stone-900">Best of</Link>
          <Link to="/safety" onClick={() => setOpen(false)} className="font-semibold text-stone-900">Safety</Link>
          {user ? (
            <>
              <Link to="/saved" onClick={() => setOpen(false)} className="font-semibold text-stone-900">Saved</Link>
              <button onClick={() => { setOpen(false); signOut(); }} className="text-left font-semibold text-stone-900">Sign out</button>
            </>
          ) : (
            <button onClick={() => signIn(null, "Sign in to save your favourite waterfalls, track ones you've visited, leave reviews and ask questions.")} className="btn-at btn-at-green text-center">Sign in with Google</button>
          )}
        </div>
      )}
    </header>
  );
}

function NavItem({ to, label, overlay }) {
  return (
    <NavLink
      to={to}
      data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) => {
        const base = "text-sm font-semibold transition-opacity hover:opacity-80";
        const color = overlay ? "text-white" : "text-stone-900";
        return `${base} ${color} ${isActive ? "underline underline-offset-8" : ""}`;
      }}
    >
      {label}
    </NavLink>
  );
}

function LogoMark({ color = "#0E1411" }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M12 3 L21 19 H3 Z M9 19 L12 13 L15 19 Z" fill={color} />
    </svg>
  );
}
